import React, { useMemo, useState } from 'react';
import { Building2, Users, ChevronRight, Activity, AlertTriangle } from 'lucide-react';
import { ORG_TREE } from '../../data/orgTree';
import { useHGS } from '../../context/HGSContext';
import { cn } from '@/lib/utils';

/**
 * Live counts per dept (responses + countermeasures), scoped by the global
 * formResponses + countermeasures collections in HGSContext.
 * Each dept node aggregates its own. Division and OH nodes roll up.
 */
function useOrgCounts() {
  const { formResponses, countermeasures } = useHGS();
  return useMemo(() => {
    const perDept = {};

    Object.values(formResponses || {}).forEach((r) => {
      const id = r?.submittedDepartment;
      if (!id) return;
      if (!perDept[id]) perDept[id] = { responses: 0, finalApproved: 0, escalated: 0 };
      perDept[id].responses += 1;
      if (r.workflowStatus === 'FinalApproved') perDept[id].finalApproved += 1;
    });

    (countermeasures || []).forEach((c) => {
      const id = c.department;
      if (!id) return;
      if (!perDept[id]) perDept[id] = { responses: 0, finalApproved: 0, escalated: 0 };
      if (c.escalated || c.daysOverdue > 30) perDept[id].escalated += 1;
    });

    const sumDept = (deptIds) => {
      const acc = { responses: 0, finalApproved: 0, escalated: 0 };
      deptIds.forEach((id) => {
        const d = perDept[id] || {};
        acc.responses += d.responses || 0;
        acc.finalApproved += d.finalApproved || 0;
        acc.escalated += d.escalated || 0;
      });
      return acc;
    };

    return { perDept, sumDept };
  }, [formResponses, countermeasures]);
}

function NodePill({ tone, count, label }) {
  if (!count) return null;
  return (
    <span className={cn(
      'inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold tabular-nums',
      tone === 'red' && 'bg-red-100 text-[#CC0000]',
      tone === 'emerald' && 'bg-emerald-100 text-emerald-700',
      tone === 'sky' && 'bg-sky-100 text-sky-700',
    )}>
      {label} {count}
    </span>
  );
}

export default function OrgChartPanel() {
  const { perDept, sumDept } = useOrgCounts();
  const [activeDeptId, setActiveDeptId] = useState(null);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="org-chart-panel">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-base font-bold text-slate-900">Live Org Map</h3>
          <p className="text-xs text-slate-500">
            Operating Head › Division › Department · live response counts. Click any node to inspect.
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-bold">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          LIVE
        </span>
      </div>

      <div className="grid grid-cols-12 gap-3" data-testid="org-chart-grid">
        {/* Column headers */}
        <div className="col-span-3 text-[10px] font-bold uppercase tracking-wider text-slate-500 pb-1 border-b border-slate-100">Operating Head</div>
        <div className="col-span-4 text-[10px] font-bold uppercase tracking-wider text-slate-500 pb-1 border-b border-slate-100">Division</div>
        <div className="col-span-5 text-[10px] font-bold uppercase tracking-wider text-slate-500 pb-1 border-b border-slate-100">Departments</div>

        {Object.values(ORG_TREE).map((oh) => {
          const ohDeptIds = Object.values(oh.divisions).flatMap((d) => Object.keys(d.departments));
          const ohRoll = sumDept(ohDeptIds);

          return (
            <React.Fragment key={oh.id}>
              {/* OH node spans rows for this domain */}
              <div className="col-span-3 pt-3">
                <div
                  className="rounded-lg border-2 border-slate-300 bg-gradient-to-br from-slate-900 to-slate-800 text-white p-3 shadow-sm sticky top-0"
                  data-testid={`org-oh-${oh.id}`}
                  style={{ borderLeftColor: oh.color, borderLeftWidth: 4 }}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="w-3.5 h-3.5 opacity-70" />
                    <div className="text-[9px] font-bold uppercase tracking-wider opacity-70">Operating Head</div>
                  </div>
                  <div className="text-sm font-bold leading-tight">{oh.label}</div>
                  <div className="text-[10px] opacity-75 mt-0.5 truncate">{oh.head.name}</div>
                  <div className="flex items-center gap-1 mt-2 flex-wrap">
                    <NodePill tone="sky" count={ohRoll.responses} label="resp" />
                    <NodePill tone="emerald" count={ohRoll.finalApproved} label="final" />
                    <NodePill tone="red" count={ohRoll.escalated} label="SLA" />
                  </div>
                </div>
              </div>

              {/* Divisions + their departments — one row per division */}
              <div className="col-span-9 pt-3 space-y-2">
                {Object.values(oh.divisions).map((div) => {
                  const deptIds = Object.keys(div.departments);
                  const divRoll = sumDept(deptIds);
                  return (
                    <div key={div.id} className="grid grid-cols-9 gap-3 items-stretch" data-testid={`org-row-${div.id}`}>
                      {/* Division node */}
                      <div className="col-span-4 relative">
                        {/* Connector line from OH to Division */}
                        <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-3 h-px bg-slate-300" />
                        <div
                          className="h-full rounded-lg border-l-4 border-slate-200 bg-slate-50/60 hover:bg-slate-50 p-3 transition cursor-default"
                          style={{ borderLeftColor: oh.color }}
                          data-testid={`org-div-${div.id}`}
                        >
                          <div className="flex items-center gap-1.5 mb-1">
                            <Building2 className="w-3 h-3 text-slate-400" />
                            <div className="text-[9px] font-bold uppercase tracking-wider text-slate-500">Division</div>
                          </div>
                          <div className="text-sm font-bold text-slate-900 leading-tight">{div.label}</div>
                          <div className="text-[10px] text-slate-500 mt-0.5 truncate">{div.head.name} · {deptIds.length} depts</div>
                          <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                            <NodePill tone="sky" count={divRoll.responses} label="resp" />
                            <NodePill tone="emerald" count={divRoll.finalApproved} label="final" />
                            <NodePill tone="red" count={divRoll.escalated} label="SLA" />
                          </div>
                        </div>
                      </div>

                      {/* Departments */}
                      <div className="col-span-5 grid grid-cols-2 gap-1.5 relative">
                        {/* Connector line from Division to dept group */}
                        <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-3 h-px bg-slate-300" />
                        {Object.values(div.departments).map((dept) => {
                          const stats = perDept[dept.id] || { responses: 0, finalApproved: 0, escalated: 0 };
                          const isActive = activeDeptId === dept.id;
                          const hasActivity = stats.responses > 0;
                          return (
                            <button
                              key={dept.id}
                              data-testid={`org-dept-${dept.id}`}
                              onClick={() => setActiveDeptId((cur) => (cur === dept.id ? null : dept.id))}
                              className={cn(
                                'text-left p-2 rounded-md border text-[10px] transition group',
                                isActive
                                  ? 'border-[#CC0000] bg-[#CC0000]/5 shadow-sm'
                                  : hasActivity
                                    ? 'border-slate-200 bg-white hover:border-[#CC0000]/40'
                                    : 'border-slate-100 bg-slate-50/50 hover:bg-white hover:border-slate-200'
                              )}
                            >
                              <div className="flex items-center justify-between gap-1">
                                <div className="font-bold text-slate-900 truncate flex-1" title={dept.label}>{dept.label}</div>
                                <ChevronRight className={cn('w-2.5 h-2.5 flex-shrink-0', isActive ? 'text-[#CC0000]' : 'text-slate-300 group-hover:text-slate-500')} />
                              </div>
                              <div className="text-[9px] text-slate-500 truncate">{dept.head.name}</div>
                              <div className="flex items-center gap-1 mt-1 flex-wrap min-h-[14px]">
                                {stats.responses > 0 && (
                                  <span className="inline-flex items-center gap-0.5 text-sky-700 font-bold">
                                    <Activity className="w-2.5 h-2.5" />{stats.responses}
                                  </span>
                                )}
                                {stats.escalated > 0 && (
                                  <span className="inline-flex items-center gap-0.5 text-[#CC0000] font-bold">
                                    <AlertTriangle className="w-2.5 h-2.5" />{stats.escalated}
                                  </span>
                                )}
                                {stats.responses === 0 && stats.escalated === 0 && (
                                  <span className="text-slate-300">—</span>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Selected dept detail strip */}
      {activeDeptId && (() => {
        // find dept in tree
        let found = null;
        Object.values(ORG_TREE).forEach((oh) => {
          Object.values(oh.divisions).forEach((div) => {
            if (div.departments[activeDeptId]) {
              found = { oh, div, dept: div.departments[activeDeptId] };
            }
          });
        });
        if (!found) return null;
        const stats = perDept[activeDeptId] || { responses: 0, finalApproved: 0, escalated: 0 };
        return (
          <div className="mt-4 p-3 rounded-lg bg-slate-900 text-white text-xs flex items-start gap-3" data-testid="org-dept-detail">
            <div className="w-8 h-8 rounded-lg bg-[#CC0000] flex items-center justify-center flex-shrink-0 font-bold">
              {found.dept.head.avatar}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm">{found.dept.label}</div>
              <div className="text-[10px] opacity-70">
                {found.oh.label} › {found.div.label} · Head: {found.dept.head.name} ({found.dept.head.email})
              </div>
            </div>
            <div className="flex gap-3 text-[10px] flex-shrink-0">
              <div><span className="opacity-70">Responses</span> <span className="font-bold tabular-nums ml-1">{stats.responses}</span></div>
              <div><span className="opacity-70">Final</span> <span className="font-bold tabular-nums ml-1 text-emerald-400">{stats.finalApproved}</span></div>
              <div><span className="opacity-70">SLA</span> <span className={cn('font-bold tabular-nums ml-1', stats.escalated > 0 ? 'text-red-400' : 'text-slate-400')}>{stats.escalated}</span></div>
            </div>
            <button
              onClick={() => setActiveDeptId(null)}
              className="text-[10px] opacity-60 hover:opacity-100 ml-2 flex-shrink-0"
              data-testid="org-detail-close"
            >
              ✕
            </button>
          </div>
        );
      })()}
    </div>
  );
}
